import { ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useUserRole } from "@/hooks/useUserRole";
import { Loader2 } from "lucide-react";

interface AdminRouteProps {
  children: ReactNode;
}

export function AdminRoute({ children }: AdminRouteProps) {
  const { user, loading: authLoading } = useAuth();
  const { isAdmin, loading: roleLoading, loadedForUserId } = useUserRole(user?.id);

  // Show loader while:
  // 1. Auth is loading, OR
  // 2. We have a user but role hasn't loaded for THIS user yet
  const isWaitingForRoles =
    !!user?.id && (roleLoading || loadedForUserId !== user.id);

  if (authLoading || isWaitingForRoles) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // No session at all → send to login (don't bounce through dashboard)
  if (!user) {
    console.warn("[AdminRoute] No user — redirecting to /auth", {
      path: window.location.pathname,
    });
    return <Navigate to="/auth" replace />;
  }

  // Redirect non-admins to a stable signed-in route. Use /dashboard rather than
  // / so this guard never bounces through LandingRoute and masks role-load bugs.
  if (!isAdmin) {
    console.warn("[AdminRoute] User is not admin — redirecting to /dashboard", {
      userId: user.id,
      path: window.location.pathname,
      loadedForUserId,
    });
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

